import React, { useEffect, useRef, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { resolveInfoContent } from "../../utils/resolveInfoContent";
import { Link } from "react-router-dom";

/**
 * BottomInfoSheet
 * - Bottom sheet that covers 90dvh, no backdrop (background stays interactive/visible).
 * - Top banner uses bg-container; pull down on the banner to dismiss; Esc closes.
 * - Renders i18n content from `infoKey`.
 *
 * Props:
 *  open: boolean
 *  onClose: () => void
 *  infoKey: string   // e.g. "info.respiratoryRate"
 *  titleFallback?: string  // optional fallback if no i18n title found
 */
export default function BottomInfoSheet({
  open,
  onClose,
  infoKey,
  titleFallback,
}) {
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(false);
  const panelRef = useRef(null);
  const dragging = useRef(false);
  const startY = useRef(0);
  const deltaY = useRef(0);

  useEffect(() => setMounted(true), []);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const onTouchStart = (e) => {
    dragging.current = true;
    startY.current = e.touches?.[0]?.clientY ?? 0;
    deltaY.current = 0;
  };
  const onTouchMove = (e) => {
    if (!dragging.current || !panelRef.current) return;
    const y = e.touches?.[0]?.clientY ?? 0;
    const d = Math.max(0, y - startY.current); // downward only
    deltaY.current = d;
    panelRef.current.style.transform = `translateY(${d}px)`;
  };
  const onTouchEnd = () => {
    if (!panelRef.current) return;
    dragging.current = false;
    if (deltaY.current > 80) onClose?.();
    else panelRef.current.style.transform = `translateY(0)`;
  };

  // -------- reuse shared resolver --------
  const content = useMemo(
    () => (infoKey ? resolveInfoContent({ t, infoKey, titleFallback }) : null),
    [t, infoKey, titleFallback]
  );

  // Pull image data straight from i18n (optional)
  const imagePath = infoKey
    ? t(`${infoKey}.imagePath`, { defaultValue: "" })
    : "";
  const imageAlt = infoKey
    ? t(`${infoKey}.imageAlt`, {
        defaultValue: content?.title || "",
      })
    : "";
  const subtitle = infoKey
    ? t(`${infoKey}.subtitle`, { defaultValue: "" })
    : "";

  if (!mounted) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      className={`fixed inset-0 z-50 pointer-events-none ${
        open ? "" : "invisible"
      }`}
    >
      <div
        ref={panelRef}
        className={`pointer-events-auto absolute inset-x-0 bottom-0 bg-background overflow-hidden 
                    transition-transform duration-200
                    ${open ? "translate-y-0" : "translate-y-full"}`}
        style={{
          height: "100dvh",
          paddingBottom:
            "max(env(safe-area-inset-bottom), constant(safe-area-inset-bottom))",
        }}
      >
        {/* Top banner/header (bg-container) — drag here to close */}
        <div
          className="bg-container"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="w-full mx-auto px-4 py-2 flex flex-row items-center justify-between ">
            <div></div>
            {content?.title ? (
              <h1 className="subtitle-text text-primary">{content.title}</h1>
            ) : null}
            <button
              type="button"
              aria-label={t("close", { defaultValue: "Close" })}
              className="p-2"
              onClick={onClose}
            >
              <span
                className="material-symbols-outlined text-[24px] text-greyscale-1"
                aria-hidden="true"
              >
                close
              </span>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="container mx-auto p-4 h-full flex flex-col gap-4 overflow-y-auto max-w-lg">
          {content?.intro ? (
            <p className="text-typography whitespace-pre-line">
              {content.intro}
            </p>
          ) : null}
          {content?.sections?.map((s, idx) => (
            <section key={idx} className=" space-y-2">
              {s.heading && <h2 className="subtitle-text">{s.heading}</h2>}
              {s.body && (
                <p className="leading-relaxed whitespace-pre-line">{s.body}</p>
              )}
            </section>
          ))}
          {content?.singleBody ? (
            <p className="leading-relaxed whitespace-pre-line">
              {content.singleBody}
            </p>
          ) : null}
          {/* Image (matches InfoAccordion styling) */}
          {imagePath ? (
            <figure className="pt-2">
              <img
                src={imagePath}
                alt={imageAlt}
                loading="lazy"
                decoding="async"
                referrerPolicy="no-referrer"
                className="w-full max-w-lg rounded-xl border border-border/50 object-cover"
              />
              {subtitle ? (
                <figcaption className="mt-2 text-xs text-typography/70">
                  {subtitle}
                </figcaption>
              ) : null}
            </figure>
          ) : null}
          <Link to="/info" className="small-button px-4 py-2 text-sm self-end">
            {t("info.moreInfo", { defaultValue: "More Info" })}
          </Link>{" "}
        </div>
      </div>
    </div>,
    document.body
  );
}
